import type { TFunction } from "i18next";
import type { InAppImportOutcome } from "./inAppAssignmentImport";

/**
 * One sentence for the user about how an in-app import went (REFSIX-568).
 *
 * Split out from the page because it is the part with the judgement in it — a
 * scrape has more ways to end than it has to succeed, and each ending sends the
 * user somewhere different — and split out from the service because the service
 * pulls in the native WebView plugin.
 *
 * Returns undefined where saying nothing is right: the user closed the WebView
 * themselves, so they know what happened and a toast would only be noise.
 */
export function inAppImportMessage(
    outcome: InAppImportOutcome,
    t: TFunction,
    platformName: string
): string | undefined {
    const key = (name: string) => `integrations.assignments.page.${name}`;

    switch (outcome.status) {
        case "imported": {
            const changed = outcome.added + outcome.updated;
            if (changed === 0 && outcome.unchanged > 0) {
                // Nothing to do is a success, and the difference matters: the
                // referee is checking whether their new appointment arrived.
                return t(key("inAppUpToDate"), { count: outcome.unchanged });
            }
            if (changed === 0) {
                return t(key("inAppNothingFound"), { platform: platformName });
            }
            return t(key("inAppImported"), { count: changed });
        }

        // InAppImportOutcome's own status, unrelated to MatchStatusExternal.
        // oxlint-disable-next-line refsix/no-magic-enum-string
        case "cancelled":
            return undefined;

        case "notImported":
            switch (outcome.reason) {
                case "allInPast":
                    return t(key("inAppAllInPast"));
                case "tooMany":
                    return t(key("inAppTooMany"));
                case "noFixtures":
                    return t(key("inAppNothingFound"), {
                        platform: platformName,
                    });
                case "error":
                    return t(key("inAppUnreadable"), {
                        platform: platformName,
                    });
            }
            break;

        case "importFailed":
            return outcome.sessionExpired
                ? t(key("inAppSessionExpired"))
                : t(key("inAppImportFailed"));

        case "timedOut":
            return t(key("inAppTimedOut"));
    }

    return t(key("inAppImportFailed"));
}
